import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import {
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid'

import {
  createAutocompleteOperator,
  createNotEqualsAutocompleteOperator,
  ALL_STRING_OPERATORS,
  NOT_CONTAINS_STRING_OPERATOR,
  CONTAINS_STRING_OPERATOR,
} from 'constants/table-filters'

import { useActions } from 'hooks/useActions'

import { useGetRecentSalesQuery } from 'services/dashboard-analytics/api'
import { DashboardAnalyticsFilter } from 'services/dashboard-analytics/types'

import { SendTicketsModal } from 'components/bookings/Sales/SendTicketsModal'
import CustomDataGrid from 'components/table/DataGrid'
import { useAddRemoveForAnalyticsMutation } from 'services/dashboard/api'
import { showNotification } from 'utils/showNotification'

export type RecentSalesTableActions = 'SEND_TICKETS' | 'SEND_TICKETS_SINGLE' | ''

interface RecentSalesTableProps {
  filters: DashboardAnalyticsFilter[]
}

const RecentSalesTable: FC<RecentSalesTableProps> = ({ filters }) => {
  const { modal, setModal, singleRow, setSingleRow } = useActions<RecentSalesTableActions>()
  const navigate = useNavigate()

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'eventName',
        headerName: 'Event Name',
        headerAlign: 'center',
        align: 'center',
        type: 'String',
        sortable: false,
        flex: 2,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '95%',
              width: '100%',
              borderLeft: `2px solid ${row?.sentStatus === 0 ? 'red' : 'green'}`,
              pl: '8px',
            }}
          >
            {row?.eventName}
          </Box>
        ),
        filterOperators: [
          createAutocompleteOperator(
            '/api/bookings/completed-sales/distinct',
            'listing.event_category.event.name'
          ),
          createNotEqualsAutocompleteOperator(
            '/api/bookings/completed-sales/distinct',
            'listing.event_category.event.name'
          ),
          ...CONTAINS_STRING_OPERATOR,
          ...NOT_CONTAINS_STRING_OPERATOR,
        ],
      },
      {
        field: 'ticketsCount',
        headerName: 'Tickets',
        headerAlign: 'center',
        align: 'center',
        type: 'Number',
        flex: 1,
        resizable: true,
        filterable: false,
        sortable: false,
      },
      {
        field: 'eventCategoryName',
        headerName: 'Category',
        headerAlign: 'center',
        align: 'center',
        type: 'String',
        flex: 1.7,
        resizable: true,
        sortable: false,
        filterOperators: [
          createAutocompleteOperator(
            '/api/bookings/completed-sales/distinct',
            'listing.event_category.name'
          ),
          createNotEqualsAutocompleteOperator(
            '/api/bookings/completed-sales/distinct',
            'listing.event_category.name'
          ),
          ...CONTAINS_STRING_OPERATOR,
          ...NOT_CONTAINS_STRING_OPERATOR,
        ],
      },
      {
        field: 'orderId',
        headerName: 'Order ID',
        headerAlign: 'center',
        align: 'center',
        type: 'String',
        flex: 1.7,
        resizable: true,
        sortable: false,
        filterOperators: ALL_STRING_OPERATORS,
      },
      {
        field: 'platform',
        headerName: 'Platform',
        headerAlign: 'center',
        align: 'center',
        type: 'String',
        flex: 1.2,
        resizable: true,
        sortable: false,
        filterOperators: ALL_STRING_OPERATORS,
      },
      {
        flex: 0.5,
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => {
          return !params?.row?.active
            ? [
                <GridActionsCellItem
                  key={1}
                  onClick={() => {
                    setModal('SEND_TICKETS_SINGLE')
                    setSingleRow(params.row)
                  }}
                  label="Send tickets"
                  showInMenu
                />,
                <GridActionsCellItem
                  key={2}
                  onClick={() => navigate(`/bookings/listings/manage/${params.row.listingId}`)}
                  label="Go to Listing"
                  showInMenu
                />,
                <GridActionsCellItem
                key={2}
                onClick={() =>
                  handleAddRemoveForAnalytics({
                    ids: [params.row.id],
                    forAnalytics: !params.row.forAnalytics,
                  })
                }
                label={params.row.forAnalytics ? 'Remove from analysis' : 'Add to analysis'}
                showInMenu
              />,
              ]
            : [
                <GridActionsCellItem
                  key={3}
                  onClick={() => navigate(`/bookings/listings/manage/${params.row.listingId}`)}
                  label="Go to Listing"
                  showInMenu
                />,
                <GridActionsCellItem
                key={2}
                onClick={() =>
                  handleAddRemoveForAnalytics({
                    ids: [params.row.id],
                    forAnalytics: !params.row.forAnalytics,
                  })
                }
                label={params.row.forAnalytics ? 'Remove from analysis' : 'Add to analysis'}
                showInMenu
              />
              ]
        },
      },
    ],
    []
  )
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const { data, isFetching: isFetchingRecentSales } = useGetRecentSalesQuery({
    filters: filters,
    page: {
      size: paginationModel.pageSize,
      pageNumber: paginationModel.page,
    },
  },
  {
    refetchOnMountOrArgChange: true,
  }
)
const [addRemoveForAnalytics] = useAddRemoveForAnalyticsMutation()
const handleAddRemoveForAnalytics = ({
                                       ids,
                                       forAnalytics,
                                     }: {
  ids: number[]
  forAnalytics: boolean
}) => {
  addRemoveForAnalytics({ ids, forAnalytics })
    .unwrap()
    .then(() => {
      showNotification(forAnalytics ? 'Added to analysis' : 'Removed from analysis', 'success')
    })
    .catch(() => {
      showNotification('Error. Try again later', 'error')
    })
}
  return (
    <Box
      sx={{
        height: '500px',
        flex: 1.3,
        minWidth: '500px',
        boxShadow: '0px 2px 10px 2px rgba(0, 0, 0, 0.1)',
      }}
    >
      <CustomDataGrid<RecentSalesTableActions>
        rows={data?.data || []}
        columns={columns}
        columnVisibilityModel={{
          matchNumber: false,
          description: false,
          isFullyCompletedHp: false,
        }}
        rowCount={data?.page?.totalElements || 0}
        loading={isFetchingRecentSales}
        onSearchChange={() => {}}
        searchValue={''}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowHeight={80}
        setModal={setModal}
        disableColumnMenu={true} 
        toolbar={() => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: '16px',
              py: '8px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography variant="h3">Recent Sales</Typography>
          </Box>
        )}
      />
      {modal === 'SEND_TICKETS_SINGLE' && (
        <SendTicketsModal onClose={() => setModal('')} sale={{...singleRow,platformOrderId:singleRow.orderId}} />
      )}
    </Box>
  )
}

export default RecentSalesTable
